import { useEffect, useState, useCallback } from "react";
import { LifetimeBanner } from "@/components/LifetimeBanner";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { YearlyActions } from "@/components/YearlyActions";
import { AiConsentBox, AiHowDialog } from "@/components/AiConsent";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PRICING } from "@/data/categories";
import { toast } from "sonner";
import {
  CalendarBlank, Sparkle, Lock, Eye, DownloadSimple,
  CheckCircle, Feather, BookBookmark, Trash, Images,
} from "@phosphor-icons/react";

const PRODUCTS = [
  { key: "yearly_reflection", icon: CalendarBlank, blurb: "A warm AI summary of one year — its important moments, the people who mattered, lessons and personal growth." },
  { key: "lifetime_reflection", icon: Feather, blurb: "A deeper reflection across your whole life — values, turning points, relationships and how you grew." },
  { key: "life_story_ebook", icon: BookBookmark, blurb: "Your memories woven into a personal book with chapters by life season — read it, download it, print it." },
];

export async function downloadKeepsakePdf(id, title) {
  const res = await api.get(`/keepsakes/${id}/pdf`, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${(title || "keepsake").replace(/[^a-z0-9]+/gi, "-")}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function KeepsakesView({ isPro }) {
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(null);
  const [previewText, setPreviewText] = useState({});
  const [previewing, setPreviewing] = useState(null);
  const [consent, setConsent] = useState({});
  const [howOpen, setHowOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/keepsakes/meta");
      setMeta(r.data);
    } catch {
      toast.error("We couldn't load your keepsakes.");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const priceOf = (key) => PRICING.ai.find((a) => a.key === key)?.price;
  const labelOf = (key) => PRICING.ai.find((a) => a.key === key)?.label || key;

  const generate = async (type, extra = {}) => {
    setBusy(type);
    try {
      const r = await api.post("/keepsakes/generate", { type, consent: true, ...extra });
      toast.success("Your keepsake is ready.");
      await load();
      navigate(`/keepsake/${r.data.id}`);
    } catch (err) {
      const d = err.response?.data?.detail;
      if (typeof d === "string" && d.startsWith("not_enough_memories:")) {
        toast.error(d.split(":").slice(3).join(":"));
      } else {
        toast.error(formatApiErrorDetail(d) || "We couldn't craft your keepsake. Please try again.");
      }
    } finally {
      setBusy(null);
    }
  };

  const removeKeepsake = async (id) => {
    if (!window.confirm("Delete this keepsake permanently? This cannot be undone.")) return;
    try {
      await api.delete(`/keepsakes/${id}`);
      toast.success("Keepsake deleted.");
      await load();
    } catch {
      toast.error("We couldn't delete that keepsake.");
    }
  };

  const seePreview = async (type) => {
    setPreviewing(type);
    setPreviewText((s) => ({ ...s, [type]: null }));
    try {
      const r = await api.post("/keepsakes/preview", { type, consent: true });
      setPreviewText((s) => ({ ...s, [type]: r.data.preview }));
    } catch (err) {
      const d = err.response?.data?.detail;
      if (typeof d === "string" && d.startsWith("not_enough_memories:")) {
        toast.error(d.split(":").slice(3).join(":"));
      } else {
        toast.error(formatApiErrorDetail(d) || "We couldn't prepare your preview.");
      }
    } finally {
      setPreviewing(null);
    }
  };

  const ConsentBox = ({ k }) => (
    <AiConsentBox k={k} checked={consent[k]} onChange={(v) => setConsent((s) => ({ ...s, [k]: v }))} onHow={() => setHowOpen(true)} />
  );

  if (!meta) return <p className="text-[#8A7F76]">Gathering your keepsakes…</p>;

  return (
    <div className="space-y-6" data-testid="keepsakes-view">
      {!isPro && !meta.is_admin && <LifetimeBanner />}
      <div className="rounded-2xl border border-[#B08D57]/25 bg-[#F4EFE6] p-5">
        <p className="text-sm leading-relaxed text-[#4A4540]">
          <Sparkle size={16} weight="duotone" className="mr-1 inline text-[#B08D57]" />
          Keepsakes are finished, personal pieces crafted by AI <strong>only from the memories you've
          recorded</strong> — never invented. Each one is saved to your account to read, download and print
          anytime. Warm, respectful and yours alone.
        </p>
      </div>

      {PRODUCTS.map((p) => {
        const owned = meta.entitlements.includes(p.key) || meta.is_admin;
        const made = meta.generated.filter((g) => g.type === p.key);
        const threshold = meta.thresholds[p.key];
        const Icon = p.icon;
        return (
          <div key={p.key} data-testid={`keepsake-card-${p.key}`} className="rounded-2xl border border-[#4A4540]/10 bg-white p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#B08D57]/30 bg-[#F4EFE6]">
                  <Icon size={22} weight="duotone" className="text-[#B08D57]" />
                </div>
                <div>
                  <h3 className="font-serif text-xl text-[#2C2A28]">{labelOf(p.key)}</h3>
                  <p className="mt-0.5 max-w-xl text-sm text-[#8A7F76]">{p.blurb}</p>
                </div>
              </div>
              {(!owned || p.key === "yearly_reflection") && (
                <span className="shrink-0 font-serif text-lg text-[#2C2A28]">{PRICING.currency}{priceOf(p.key)}</span>
              )}
            </div>

            {/* Already generated */}
            {made.length > 0 && (
              <div className="mt-4 space-y-2">
                {made.map((g) => (
                  <div key={g.id} data-testid={`keepsake-ready-${g.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[#7EA193]/30 bg-[#EAF1EE] p-3">
                    <div className="flex items-center gap-2">
                      <CheckCircle size={18} weight="duotone" className="text-[#7EA193]" />
                      <span className="text-sm font-medium text-[#2C2A28]">{g.title}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                      {p.key === "life_story_ebook" && (
                        <Button data-testid={`ebook-photos-${g.id}`} onClick={() => navigate(`/ebook-studio?keepsake=${g.id}`)} size="sm" className="rounded-full bg-[#E07A5A] text-white hover:bg-[#d66b4b]">
                          <Images size={15} weight="duotone" className="mr-1" /> Add photos &amp; download
                        </Button>
                      )}
                      <Button data-testid={`view-keepsake-${g.id}`} onClick={() => navigate(`/keepsake/${g.id}`)} size="sm" variant="outline" className="rounded-full border-[#4A4540]/20 text-[#2C2A28]">
                        <Eye size={15} weight="duotone" className="mr-1" /> View
                      </Button>
                      <Button data-testid={`download-keepsake-${g.id}`} onClick={() => downloadKeepsakePdf(g.id, g.title)} size="sm" variant="outline" className="rounded-full border-[#4A4540]/20 text-[#2C2A28]">
                        <DownloadSimple size={15} weight="duotone" className="mr-1" /> Download
                      </Button>
                      <Button data-testid={`delete-keepsake-${g.id}`} onClick={() => removeKeepsake(g.id)} size="sm" variant="outline" className="rounded-full border-[#C0533B]/30 text-[#C0533B] hover:bg-[#C0533B] hover:text-white">
                        <Trash size={15} weight="duotone" className="mr-1" /> Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Actions */}
            <div className="mt-4">
              {p.key === "yearly_reflection" ? (
                <YearlyActions meta={meta} threshold={threshold} made={made} consentBox={<ConsentBox k={p.key} />}
                  consented={!!consent[p.key]} busy={busy === p.key} onGenerate={(year) => generate("yearly_reflection", { year })} />
              ) : !owned ? (
                p.key === "lifetime_reflection" ? (
                  <div data-testid="lifetime-preview-block" className="space-y-3">
                    {meta.total_memories < threshold ? (
                      <p data-testid="not-enough-lifetime-preview" className="text-sm text-[#8A5A2B]">
                        Add at least {threshold} memories (you have {meta.total_memories}) to see a free preview or unlock this.
                      </p>
                    ) : (
                      <>
                        <div data-testid="lifetime-coverage" className="rounded-xl border border-[#4A4540]/10 bg-white p-3 text-sm text-[#4A4540]">
                          <p>
                            <strong>What this covers:</strong> all {meta.total_memories} memories you've saved so far
                            {meta.coverage?.earliest_year ? ` — from ${meta.coverage.earliest_year} to ${meta.coverage.latest_year}` : ""}.
                          </p>
                          <p className="mt-1 text-xs text-[#8A5A2B]">
                            Memories you add later are <strong>not</strong> added to a reflection you've already
                            generated — you can always create a fresh one.
                          </p>
                        </div>
                        <p className="text-sm text-[#4A4540]">
                          Not sure yet? See a free, personalised glimpse across your life first.
                        </p>
                        <ConsentBox k={p.key} />
                        <Button
                          data-testid="see-preview-lifetime-btn"
                          disabled={previewing === p.key || !consent[p.key]}
                          onClick={() => seePreview("lifetime_reflection")}
                          variant="outline"
                          className="rounded-full border-[#B08D57] text-[#B08D57] hover:bg-[#B08D57] hover:text-white"
                        >
                          <Sparkle size={15} weight="duotone" className="mr-1" />
                          {previewing === p.key ? "Reading…" : "See a free preview"}
                        </Button>
                        {previewText[p.key] && (
                          <div data-testid="preview-result-lifetime" className="rounded-xl border border-[#7EA193]/30 bg-[#EAF1EE] p-4">
                            <p className="font-handwriting text-xl leading-snug text-[#2C2A28]">{previewText[p.key]}</p>
                            <Button data-testid="preview-unlock-lifetime-btn" onClick={() => navigate("/upgrade")} className="mt-3 rounded-full bg-[#B08D57] text-[#FDFBF7] hover:bg-[#C19A6B]">
                              <Lock size={14} weight="fill" className="mr-1.5" /> Unlock My Full Life Reflection — {PRICING.currency}{priceOf(p.key)}
                            </Button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ) : p.key === "life_story_ebook" ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <Button data-testid="preview-ebook-studio-btn" onClick={() => navigate("/ebook-studio")} variant="outline" className="rounded-full border-[#B08D57] text-[#B08D57] hover:bg-[#B08D57] hover:text-white">
                      <Eye size={15} weight="duotone" className="mr-1.5" /> Preview in the Ebook studio
                    </Button>
                    <Button data-testid={`unlock-keepsake-${p.key}`} onClick={() => navigate("/upgrade")} className="rounded-full bg-[#B08D57] text-[#FDFBF7] hover:bg-[#C19A6B]">
                      <Lock size={15} weight="fill" className="mr-1.5" /> Unlock for {PRICING.currency}{priceOf(p.key)}
                    </Button>
                  </div>
                ) : (
                  <Button data-testid={`unlock-keepsake-${p.key}`} onClick={() => navigate("/upgrade")} className="rounded-full bg-[#B08D57] text-[#FDFBF7] hover:bg-[#C19A6B]">
                    <Lock size={15} weight="fill" className="mr-1.5" /> Unlock for {PRICING.currency}{priceOf(p.key)}
                  </Button>
                )
              ) : p.key === "lifetime_reflection" ? (
                meta.total_memories < threshold ? (
                  <p data-testid="not-enough-lifetime" className="text-sm text-[#8A5A2B]">
                    Add at least {threshold} memories (you have {meta.total_memories}) and we'll craft your life reflection.
                  </p>
                ) : (
                  <div className="space-y-3">
                    <div data-testid="lifetime-coverage-owned" className="rounded-xl border border-[#4A4540]/10 bg-white p-3 text-sm text-[#4A4540]">
                      <p>
                        <strong>What this covers:</strong> all {meta.total_memories} memories you've saved so far
                        {meta.coverage?.earliest_year ? ` — from ${meta.coverage.earliest_year} to ${meta.coverage.latest_year}` : ""}.
                      </p>
                      <p className="mt-1 text-xs text-[#8A5A2B]">
                        Memories you add later are <strong>not</strong> added to a reflection you've already
                        generated — you can always create a fresh one.
                      </p>
                    </div>
                    <ConsentBox k={p.key} />
                    <Button data-testid="generate-lifetime-btn" disabled={busy === p.key || !consent[p.key]} onClick={() => generate("lifetime_reflection")} className="rounded-full bg-[#B08D57] text-[#FDFBF7] hover:bg-[#C19A6B]">
                      <Sparkle size={15} weight="duotone" className="mr-1.5" />
                      {busy === p.key ? "Reflecting…" : made.length ? "Create a fresh reflection" : "Create my reflection"}
                    </Button>
                  </div>
                )
              ) : (
                meta.total_memories < threshold ? (
                  <p data-testid="not-enough-ebook" className="text-sm text-[#8A5A2B]">
                    Add at least {threshold} memories (you have {meta.total_memories}) so your book has enough to tell.
                  </p>
                ) : (
                  <Button data-testid="review-ebook-btn" onClick={() => navigate("/ebook-studio")} className="rounded-full bg-[#B08D57] text-[#FDFBF7] hover:bg-[#C19A6B]">
                    <BookBookmark size={15} weight="duotone" className="mr-1.5" />
                    {made.length ? "Create a new edition" : "Open the Ebook studio"}
                  </Button>
                )
              )}
            </div>
          </div>
        );
      })}

      <AiHowDialog open={howOpen} onOpenChange={setHowOpen} />
    </div>
  );
}
